import type { TargetSpec } from "../heroes/module.ts";
import { groundAimPoint } from "../heroes/kitlib.ts";
import { TARGETS } from "../heroes/roster/index.ts";
import { type HeroId, Slot } from "../heroes/types.ts";
import type { Vec3 } from "../math.ts";
import { Btn, type InputCmd } from "../net/input.ts";
import { abilIdx } from "./components.ts";
import type { Sim } from "./sim.ts";

/** The key each ability slot is cast with. */
export const SLOT_BUTTON: Record<Slot, number> = {
  [Slot.Primary]: Btn.Primary,
  [Slot.Secondary]: Btn.Secondary,
  [Slot.Ability1]: Btn.Ability1,
  [Slot.Ability2]: Btn.Ability2,
  [Slot.Ultimate]: Btn.Ultimate,
  [Slot.Ability3]: Btn.Ability3,
};
const FIRE = Btn.Primary | Btn.Secondary;
const CAST = Btn.Secondary | Btn.Ability1 | Btn.Ability2 | Btn.Ability3 | Btn.Ultimate;

export const targetSpec = (sim: Sim, eid: number, slot: Slot): TargetSpec | undefined => TARGETS[sim.c.Player.hero[eid] as HeroId]?.[slot];

/** The slot being aimed right now, if any. */
export function targetingSlot(sim: Sim, eid: number): Slot | undefined {
  const s = sim.c.Player.aimSlot[eid]!;
  return s > 0 ? ((s - 1) as Slot) : undefined;
}

/** Starts a "kit" target: the next step of an ability already under way. */
export function beginTargeting(sim: Sim, eid: number, slot: Slot) {
  sim.c.Player.aimSlot[eid] = slot + 1;
  sim.c.Player.aimTicks[eid] = 0;
}

export function endTargeting(sim: Sim, eid: number) {
  sim.c.Player.aimSlot[eid] = 0;
  sim.c.Player.aimTicks[eid] = 0;
}

/** True during the tick a "kit" target for `slot` was confirmed. */
export const targetConfirmed = (sim: Sim, eid: number, slot: Slot) => sim.c.Player.aimDone[eid] === slot + 1;

/**
 * Where `slot`'s target is: the grounded crosshair point within its range (or the caster's own
 * position), facing the caster's view. The preview and the cast both use this, so they agree.
 */
export function targetPoint(sim: Sim, eid: number, slot: Slot): { pos: Vec3; yaw: number } {
  const spec = targetSpec(sim, eid, slot);
  const yaw = sim.c.Look.yaw[eid]!;
  if (!spec || spec.self) return { pos: sim.posOf(eid), yaw };
  return { pos: groundAimPoint(sim, eid, spec.range, spec.clearance ?? 0), yaw };
}

/** `targetPoint` for a look direction other than the last simulated one (the client's preview between ticks). */
export function previewTargetPoint(sim: Sim, eid: number, slot: Slot, yaw: number, pitch: number) {
  const c = sim.c;
  const y0 = c.Look.yaw[eid]!;
  const p0 = c.Look.pitch[eid]!;
  c.Look.yaw[eid] = yaw;
  c.Look.pitch[eid] = pitch;
  try {
    return targetPoint(sim, eid, slot);
  } finally {
    c.Look.yaw[eid] = y0;
    c.Look.pitch[eid] = p0;
  }
}

const slotReady = (sim: Sim, eid: number, slot: Slot) =>
  slot === Slot.Ultimate ? sim.c.Ult.charge[eid]! >= 1 : sim.c.Abil.charges[abilIdx(eid, slot)]! > 0;

/**
 * Runs before the kit each tick (predicted). While a target is up, LMB and RMB belong to it: the
 * kit doesn't see them, and whichever one ended it stays hidden until released, so confirming
 * with LMB doesn't also fire. Returns the input and pressed edges the kit should see.
 */
export function stepTargeting(sim: Sim, eid: number, input: InputCmd, pressed: number, canAct: boolean): { input: InputCmd; pressed: number } {
  const c = sim.c;
  const P = c.Player;
  P.aimDone[eid] = 0;
  P.aimMask[eid] = P.aimMask[eid]! & input.buttons;
  const quick = !!sim.players.get(eid)?.bot;
  let buttons = input.buttons;
  let aiming = targetingSlot(sim, eid);
  if (aiming === undefined && canAct && !quick) {
    for (const [k, spec] of Object.entries(TARGETS[P.hero[eid] as HeroId] ?? {})) {
      const slot = Number(k) as Slot;
      const btn = SLOT_BUTTON[slot];
      if ((spec.start ?? "press") !== "press" || !(pressed & btn) || !slotReady(sim, eid, slot)) continue;
      beginTargeting(sim, eid, slot);
      pressed &= ~btn;
      buttons &= ~btn;
      // The press is held back until it's confirmed.
      P.aimMask[eid] = 0;
      return mask(sim, eid, input, buttons, pressed);
    }
  }
  if (aiming !== undefined) {
    const spec = targetSpec(sim, eid, aiming);
    const btn = SLOT_BUTTON[aiming];
    const kit = spec?.start === "kit";
    P.aimTicks[eid] = P.aimTicks[eid]! + 1;
    const fire = (quick ? buttons : pressed) & Btn.Primary;
    const timedOut = kit && spec?.timeout !== undefined && P.aimTicks[eid]! >= Math.round(spec.timeout * 60);
    const confirm = !!spec && (fire || (!kit && pressed & btn) || timedOut);
    // Backing out: RMB, another ability, or being disabled. A kit step can't be backed out of.
    const cancel = !spec || !P.alive[eid] || (!kit && (!!(pressed & Btn.Secondary) || !!(pressed & CAST & ~btn) || !canAct || !slotReady(sim, eid, aiming)));
    if (confirm && !cancel) {
      endTargeting(sim, eid);
      P.aimDone[eid] = aiming + 1;
      P.aimMask[eid] = buttons & FIRE;
      buttons &= ~FIRE;
      pressed &= ~FIRE;
      if (!kit) {
        pressed |= btn;
        buttons |= btn;
      }
      return mask(sim, eid, input, buttons, pressed);
    }
    if (cancel) {
      endTargeting(sim, eid);
      P.aimMask[eid] = buttons & FIRE;
      buttons &= ~FIRE;
      pressed &= ~(FIRE | btn);
      return mask(sim, eid, input, buttons, pressed);
    }
    buttons &= ~(FIRE | btn);
    pressed &= ~(FIRE | btn);
  }
  return mask(sim, eid, input, buttons, pressed);
}

function mask(sim: Sim, eid: number, input: InputCmd, buttons: number, pressed: number) {
  const m = sim.c.Player.aimMask[eid]!;
  buttons &= ~m;
  pressed &= ~m;
  return { input: buttons === input.buttons ? input : { ...input, buttons }, pressed };
}
